import { IModularFamily } from '../redux/slices/ModularEnrolmentSlice/types';
import { IDependent } from '../redux/slices/PolicyListSlice/types';
import { FlagsmithFlags, isFlagsmithFeatureEnabled } from './flagsmith';

export interface IMappings {
    totalDependents: number;
    value: string;
    dependents: { relation: string; number: number }[] | null;
    description?: string;
}

export type IFamilyDefinition = Record<string, IMappings>;

export const familyDefinition: IFamilyDefinition = {
    'Self': {
        totalDependents: 0,
        value: 'Self',
        description: 'Self',
        dependents: null
    },
    'Self + Spouse': {
        totalDependents: 1,
        value: 'Self + Spouse',
        description: 'Self & Spouse',
        dependents: [
            {
                relation: 'spouse',
                number: 1
            }
        ]
    },
    'Self + Spouse + 1 Kid': {
        totalDependents: 2,
        value: 'Self + Spouse + 1 Kid',
        description: 'Self, Spouse & 1 Child',
        dependents: [
            {
                relation: 'spouse',
                number: 1
            },
            {
                relation: 'child',
                number: 1
            }
        ]
    },
    'Self + Spouse + 2 Kids': {
        totalDependents: 3,
        value: 'Self + Spouse + 2 Kids',
        description: 'Self, Spouse & 2 Children',
        dependents: [
            {
                relation: 'spouse',
                number: 1
            },
            {
                relation: 'child',
                number: 2
            }
        ]
    },
    'Self + Spouse + 3 Kids': {
        totalDependents: 4,
        value: 'Self + Spouse + 3 Kids',
        description: 'Self, Spouse & 3 Children',
        dependents: [
            {
                relation: 'spouse',
                number: 1
            },
            {
                relation: 'child',
                number: 3
            }
        ]
    },
    'Self + Spouse + 4 Kids': {
        totalDependents: 5,
        value: 'Self + Spouse + 4 Kids',
        description: 'Self, Spouse & 4 Children',
        dependents: [
            {
                relation: 'spouse',
                number: 1
            },
            {
                relation: 'child',
                number: 4
            }
        ]
    },
    'Self + 2 Siblings': {
        totalDependents: 2,
        value: 'Self + 2 Siblings',
        description: 'Self & 2 Siblings',
        dependents: [
            {
                relation: 'sibling',
                number: 2
            }
        ]
    },
    'Self + Spouse + 2 Kids + 2 Siblings': {
        totalDependents: 5,
        value: 'Self + Spouse + 2 Kids + 2 Siblings',
        description: 'Self, Spouse, 2 Children & 2 Siblings',
        dependents: [
            {
                relation: 'spouse',
                number: 1
            },
            {
                relation: 'child',
                number: 2
            },
            {
                relation: 'sibling',
                number: 2
            }
        ]
    },
    'Self + Spouse + 2 Kids + Parents': {
        totalDependents: 5,
        value: 'Self + Spouse + 2 Kids + Parents',
        description: 'Self, Spouse, 2 Children & 2 Parents',
        dependents: [
            {
                relation: 'spouse',
                number: 1
            },
            {
                relation: 'child',
                number: 2
            },
            {
                relation: 'parent',
                number: 2
            }
        ]
    },
    'Self + Spouse + 3 Kids + Parents': {
        totalDependents: 6,
        value: 'Self + Spouse + 3 Kids + Parents',
        description: 'Self, Spouse, 3 Children & 2 Parents',
        dependents: [
            {
                relation: 'spouse',
                number: 1
            },
            {
                relation: 'child',
                number: 3
            },
            {
                relation: 'parent',
                number: 2
            }
        ]
    },
    'Self + Spouse + 4 Kids + Parents': {
        totalDependents: 7,
        value: 'Self + Spouse + 4 Kids + Parents',
        description: 'Self, Spouse, 4 Children & 2 Parents',
        dependents: [
            {
                relation: 'spouse',
                number: 1
            },
            {
                relation: 'child',
                number: 4
            },
            {
                relation: 'parent',
                number: 2
            }
        ]
    },
    'Self + Spouse + 2 Kids + Parents (including in-laws)': {
        totalDependents: 5,
        value: 'Self + Spouse + 2 Kids + Parents (including in-laws)',
        description: 'Self, Spouse, 2 Children, any 2 of Parents and Parents-in-laws',
        dependents: [
            {
                relation: 'spouse',
                number: 1
            },
            {
                relation: 'child',
                number: 2
            },
            {
                relation: 'parent/parent-in-law',
                number: 2
            }
        ]
    },
    'Self + Spouse + 3 Kids + Parents (including in-laws)': {
        totalDependents: 6,
        value: 'Self + Spouse + 3 Kids + Parents (including in-laws)',
        description: 'Self, Spouse, 3 Children, any 2 of Parents and Parents-in-laws',
        dependents: [
            {
                relation: 'spouse',
                number: 1
            },
            {
                relation: 'child',
                number: 3
            },
            {
                relation: 'parent/parent-in-law',
                number: 2
            }
        ]
    },
    'Self + Spouse + 4 Kids + Parents (including in-laws)': {
        totalDependents: 7,
        value: 'Self + Spouse + 4 Kids + Parents (including in-laws)',
        description: 'Self, Spouse, 4 Children, any 2 of Parents and Parents-in-laws',
        dependents: [
            {
                relation: 'spouse',
                number: 1
            },
            {
                relation: 'child',
                number: 4
            },
            {
                relation: 'parent/parent-in-law',
                number: 2
            }
        ]
    },
    'Self + Spouse + 2 Kids + Parents OR parent-in-laws': {
        totalDependents: 5,
        value: 'Self + Spouse + 2 Kids + Parents OR parent-in-laws',
        description: 'Self, Spouse, 2 Children, Parents or Parents-in-laws',
        dependents: [
            {
                relation: 'spouse',
                number: 1
            },
            {
                relation: 'child',
                number: 2
            },
            {
                relation: 'parent|parent-in-law',
                number: 2
            }
        ]
    },
    'Self + Spouse + 3 Kids + Parents OR parent-in-laws': {
        totalDependents: 6,
        value: 'Self + Spouse + 3 Kids + Parents OR parent-in-laws',
        description: 'Self, Spouse, 3 Children, Parents or Parents-in-laws',
        dependents: [
            {
                relation: 'spouse',
                number: 1
            },
            {
                relation: 'child',
                number: 3
            },
            {
                relation: 'parent|parent-in-law',
                number: 2
            }
        ]
    },
    'Self + Spouse + 4 Kids + Parents OR parent-in-laws': {
        totalDependents: 7,
        value: 'Self + Spouse + 4 Kids + Parents OR parent-in-laws',
        description: 'Self, Spouse, 4 Children, Parents or Parents-in-laws',
        dependents: [
            {
                relation: 'spouse',
                number: 1
            },
            {
                relation: 'child',
                number: 4
            },
            {
                relation: 'parent|parent-in-law',
                number: 2
            }
        ]
    },
    'Self + Spouse + 2 Kids + Parents + Parents-in-law': {
        totalDependents: 7,
        value: 'Self + Spouse + 2 Kids + Parents + Parents-in-law',
        description: 'Self, Spouse, 2 Children, Parents & Parents-in-laws',
        dependents: [
            {
                relation: 'spouse',
                number: 1
            },
            {
                relation: 'child',
                number: 2
            },
            {
                relation: 'parent',
                number: 2
            },
            {
                relation: 'parent-in-law',
                number: 2
            }
        ]
    },
    'Self + Spouse + 3 Kids + Parents + Parents-in-law': {
        totalDependents: 8,
        value: 'Self + Spouse + 3 Kids + Parents + Parents-in-law',
        description: 'Self, Spouse, 3 Children, Parents & Parents-in-laws',
        dependents: [
            {
                relation: 'spouse',
                number: 1
            },
            {
                relation: 'child',
                number: 3
            },
            {
                relation: 'parent',
                number: 2
            },
            {
                relation: 'parent-in-law',
                number: 2
            }
        ]
    },
    'Self + Spouse + 4 Kids + Parents + Parents-in-law': {
        totalDependents: 9,
        value: 'Self + Spouse + 4 Kids + Parents + Parents-in-law',
        description: 'Self, Spouse, 4 Children, Parents & Parents-in-laws',
        dependents: [
            {
                relation: 'spouse',
                number: 1
            },
            {
                relation: 'child',
                number: 4
            },
            {
                relation: 'parent',
                number: 2
            },
            {
                relation: 'parent-in-law',
                number: 2
            }
        ]
    },
    'Self + Spouse + 2 Kids + Parents + 1 Parent-in-law': {
        totalDependents: 6,
        value: 'Self + Spouse + 2 Kids + Parents + 1 Parent-in-law',
        description: 'Self, Spouse, 2 Children, Parents & 1 Parent-in-law',
        dependents: [
            {
                relation: 'spouse',
                number: 1
            },
            {
                relation: 'child',
                number: 2
            },
            {
                relation: 'parent',
                number: 2
            },
            {
                relation: 'parent-in-law',
                number: 1
            }
        ]
    },
    'Self + Spouse + 2 Kids (including siblings)': {
        totalDependents: 4,
        value: 'Self + Spouse + 2 Kids (including siblings)',
        description: 'Self, Spouse, any 2 of Children and Siblings',
        dependents: [
            {
                relation: 'spouse',
                number: 1
            },
            {
                relation: 'child/sibling',
                number: 2
            }
        ]
    },
    'Self + Spouse + 2 Kids (including siblings) + Parents OR parent-in-laws': {
        totalDependents: 6,
        value: 'Self + Spouse + 2 Kids (including siblings) + Parents OR parent-in-laws',
        description: 'Self, Spouse, any 2 of Children and Siblings, Parents or Parents-in-laws',
        dependents: [
            {
                relation: 'spouse',
                number: 1
            },
            {
                relation: 'child/sibling',
                number: 2
            },
            {
                relation: 'parent|parent-in-law',
                number: 2
            }
        ]
    },
    'Self + Spouse + 2 Kids (including siblings) + Parents + Parents-in-law': {
        totalDependents: 8,
        value: 'Self + Spouse + 2 Kids (including siblings) + Parents + Parents-in-law',
        description: 'Self, Spouse, any 2 of Children and Siblings, Parents & Parents-in-laws',
        dependents: [
            {
                relation: 'spouse',
                number: 1
            },
            {
                relation: 'child/sibling',
                number: 2
            },
            {
                relation: 'parent',
                number: 2
            },
            {
                relation: 'parent-in-law',
                number: 2
            }
        ]
    },
    'Parents Only': {
        totalDependents: 2,
        value: 'Parents Only',
        description: 'Parents Only',
        dependents: [
            {
                relation: 'parent',
                number: 2
            }
        ]
    },
    'Parents (including in-laws)': {
        totalDependents: 2,
        value: 'Parents (including in-laws)',
        description: 'Any 2 of Parents and Parents-in-laws',
        dependents: [
            {
                relation: 'parent/parent-in-law',
                number: 2
            }
        ]
    },
    'Parents OR parents-in-law': {
        totalDependents: 2,
        value: 'Parents OR parents-in-law',
        description: 'Parents or Parents-in-laws',
        dependents: [
            {
                relation: 'parent|parent-in-law',
                number: 2
            }
        ]
    },
    'Parents + Parents-in-law': {
        totalDependents: 4,
        value: 'Parents + Parents-in-law',
        description: 'Parents & Parents-in-laws',
        dependents: [
            {
                relation: 'parent',
                number: 2
            },
            {
                relation: 'parent-in-law',
                number: 2
            }
        ]
    }
};

export const getDependentMappings = (
    dependents: IDependent[] | IModularFamily[],
    familyStruture: string
): IMappings => {
    const value: IMappings = {
        value: familyStruture,
        dependents: [],
        totalDependents: 0
    };

    const newDependents: { relation: string; number: number }[] = [];

    const isIDependentArray = (dependents: IDependent[] | IModularFamily[]): dependents is IDependent[] => {
        return (dependents as IDependent[])[0]?.relationship !== undefined;
    };

    if (isIDependentArray(dependents)) {
        const totalDependentsExceptSelf = dependents?.filter((dependent) => {
            return dependent.relationship.toLocaleLowerCase() !== 'self';
        });

        totalDependentsExceptSelf.forEach((dependent) => {
            value.totalDependents += 1;
            value.dependents?.push({ relation: dependent.relationship.toLocaleLowerCase(), number: 1 });
        });

        value.dependents?.forEach((item) => {
            const items = value.dependents?.filter((itemAlt) => {
                return item.relation.toLocaleLowerCase() === itemAlt.relation.toLocaleLowerCase();
            });
            const index = newDependents.findIndex((itemAlt) => {
                return itemAlt.relation === item.relation;
            });
            if (index == -1 && items?.length) {
                newDependents.push({ relation: item.relation, number: items?.length });
            }
        });

        value.dependents = newDependents;
    }
    return value;
};

export const getFamilyDefinitionDescription = (familyDef: keyof IFamilyDefinition): string => {
    return familyDefinition[familyDef]?.description || familyDef;
};

export const getIneligibleFamilyMembers = (familyStruture: string, family: IModularFamily[]): IModularFamily[] => {
    const familyDef = structuredClone(familyDefinition[familyStruture]);
    if (!familyDef || !familyDef.dependents) return [];
    let deletableDependents = family.filter((member) => member.relationship.toLowerCase() !== 'self');
    const isPartnerSupportEnabled = isFlagsmithFeatureEnabled(FlagsmithFlags.PARTNER_SUPPORT);
    if (isPartnerSupportEnabled) {
        familyDef.dependents = familyDef.dependents.map((d) =>
            d.relation === 'spouse' ? { ...d, relation: 'spouse|partner' } : d
        );
    }

    familyDef.dependents.forEach((d) => {
        let count = 0;
        deletableDependents = deletableDependents.filter((member) => {
            const relations = d.relation.replace('/', '|').split('|');
            if (relations.includes(member.relationship.toLowerCase()) && count < d.number) {
                count += 1;
                return false; // Remove this member from the list
            }
            return true; // Keep this member in the list
        });
    });
    return deletableDependents;
};

export const isBetterFamilyStructure = (prev: string, curr: string): boolean => {
    const prevDef = familyDefinition[prev];
    const currDef = familyDefinition[curr];
    if (!prevDef || !currDef) return false;

    // Check if the current structure has more total dependents than the previous one
    if (currDef.totalDependents > prevDef.totalDependents) return true;
    if (currDef.totalDependents < prevDef.totalDependents) return false;

    const prevSupported = prevDef.dependents?.flatMap((d) => d.relation?.replace('/', '|').split('|')) || [];
    const currSupported = currDef.dependents?.flatMap((d) => d.relation?.replace('/', '|').split('|')) || [];
    return currSupported.length > prevSupported.length;
};
